/* global pendo */
import { useNavigate } from "react-router-dom";
import {
  setUser,
  clearUser,
  clearToken,
  setToken,
  selectUser,
  selectToken,
} from "../redux/features/appData";
import { useDispatch, useSelector } from "react-redux";
import { AuthSource, loginUser } from "../services/AuthService";
import { apiGetUserByEmail } from "../services/UserService";
import { setAxiosAuthToken } from "../services/client";
import config from "environment";
import {
  clearExtensionLogoutFlag,
  getExtensionLogoutFlag,
} from "../utils/localStorage";

const defaultRoute = config.defaultRoute;

export type AuthContextProviderValue = {
  login: (payload: LoginPayload) => void;
  logout: () => void;
  checkStatus: () => void;
};

export type LoginPayload = {
  email: string;
  passwordKey: string;
  authSource: AuthSource;
};

export type VerifyEmailPayload = {
  userId: string;
  email: string;
  token: string;
};

export enum AuthErrorCodes {
  AUTH_INCORRECT_PASSWORD = "AUTH_INCORRECT_PASSWORD",
  AUTH_NO_USER_FOUND = "AUTH_NO_USER_FOUND",
  AUTH_FAILED = "AUTH_FAILED",
  AUTH_SYSTEM_USER = "AUTH_SYSTEM_USER",
}

export type LoginResponse = {
  success: boolean;
  error?: string;
};

export const useAuth = () => {
  const user = useSelector(selectUser);
  const token = useSelector(selectToken);

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const login = async (
    payload: LoginPayload,
    onboardingState: string = "none"
  ): Promise<LoginResponse> => {
    //console.log("[useAuth/login] payload: ", payload);
    const newToken = await loginUser(payload);

    console.log("[useAuth/login] newToken: ", newToken);
    if (newToken === AuthErrorCodes.AUTH_SYSTEM_USER) {
      return { success: false, error: "Invalid account." };
    }

    if (newToken === AuthErrorCodes.AUTH_NO_USER_FOUND) {
      // toast("No user exists for that email", toastFail);
      return { success: false, error: "No user exists for that email" };
    }

    if (newToken === AuthErrorCodes.AUTH_INCORRECT_PASSWORD) {
      // toast("Incorrect Password", toastFail);
      return {
        success: false,
        error: "Incorrect password or Google authed account.",
      };
    }

    if (newToken === AuthErrorCodes.AUTH_FAILED) {
      // toast("Login Failed", toastFail);
      return { success: false, error: "Login Failed" };
    }

    if (newToken) {
      setAxiosAuthToken(newToken);
      const [loggedInUser] = await Promise.all([
        apiGetUserByEmail(payload.email),
      ]);

      dispatch(setUser(loggedInUser));
      dispatch(setToken(newToken));

      // flush out the local storage for comment user in case they started as a logged out 
      // commenter
      localStorage.removeItem('publicName');
      localStorage.removeItem('publicEmail');

      // if we are a new user, don't go to the stash yet. If we are just logging in, please do
      // NR** just messed with this
      const extensionLogoutFlag = getExtensionLogoutFlag();
      if (extensionLogoutFlag) {
        navigate("/extension/landing");
        clearExtensionLogoutFlag();
      } else {
        navigate(defaultRoute);
      }

      return { success: true };
    }

    // toast("Error Logging In", toastFail);
    return { success: false, error: "Error Logging In" };
  };

  const checkStatus = (logoutOnFail: boolean = true) => {
    if (!token || !user) {
      if (logoutOnFail) {
        logout();
        return;
      }
    }

    setAxiosAuthToken(token);

    // if (!!pendo) {
    //   const pendoReady = pendo.isReady && pendo.isReady();
    //   if (!pendoReady) {
    //     const pendoPayload = user
    //       ? ({
    //           visitor: { ...user, env: window.location.hostname },
    //           account: { ...user, env: window.location.hostname },
    //         } as any)
    //       : undefined;

    //     pendo.identify(pendoPayload);
    //   }
    // }
  };

  const logout = () => {
    dispatch(clearUser());
    dispatch(clearToken());

    // @ts-ignore
    // if (!!window.pendo) {
    //   // @ts-ignore
    //   pendo.clearSession();
    // }

    setAxiosAuthToken(undefined);

    navigate("/logout", {
      replace: true,
    });
  };

  return {
    login,
    logout,
    checkStatus,
  };
};
